import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";

/**
 * ─── DropdownMenu ─────────────────────────────────────────────────────────
 * Overflow menu (`⋯` by default). Used for row actions and secondary menus.
 *
 * Positioning
 *   Menu is rendered via `createPortal(document.body)` so it escapes every
 *   scroll container and never gets clipped by table overflow. Placement is
 *   computed from the live trigger rect + viewport:
 *     - Preferred: bottom-end (bottom-start when align="left")
 *     - Auto-flip: switches to top-* when there isn't enough space below
 *     - Shift:     horizontally clamped so it can't leave the viewport
 *   Recomputed on scroll (capture:true → nested scrollables) and on resize.
 *
 * Props (unchanged public API)
 *   trigger    ReactNode — contents of the trigger button (default: `⋯` icon)
 *   items      Array<Item>   where Item is one of:
 *                { key, label, icon?, onClick, danger?, disabled?, shortcut? }
 *              | { key, separator: true }
 *              | { key, sectionLabel: string }
 *   align      "right" | "left"  (default "right") — visual alignment relative
 *              to the trigger. Ignored in the flipped-vertical direction, only
 *              controls horizontal end.
 *   ariaLabel  string — accessibility label for the trigger.
 *   testId     string
 *
 * Keyboard
 *   Enter/Space on trigger  → opens menu, focuses first item
 *   ArrowDown / ArrowUp     → roving focus over enabled items
 *   Home / End              → first / last item
 *   Enter / Space on item   → activate
 *   Escape                  → close, return focus to trigger
 */

// Distance between trigger and menu, and viewport padding for shift().
const MENU_OFFSET  = 4;
const VIEWPORT_PAD = 8;

// Menu render — separated so measurement can happen after commit.
const Menu = React.forwardRef(function Menu(
  { style, align, ariaLabel, testId, children, dataFlipped },
  ref,
) {
  return (
    <div
      ref={ref}
      className="ds-dropdown__menu"
      data-align={align}
      data-flipped={dataFlipped ? "true" : undefined}
      role="menu"
      aria-label={ariaLabel}
      data-testid={testId ? `${testId}-menu` : undefined}
      style={style}
    >
      {children}
    </div>
  );
});

export default function DropdownMenu({
  trigger, items = [], align = "right", ariaLabel = "More actions", testId,
}) {
  const [open, setOpen] = useState(false);
  const wrap        = useRef(null);
  const triggerRef  = useRef(null);
  const menuRef     = useRef(null);
  const itemRefs    = useRef([]);

  // Positioning state. Uses `position: fixed` on the portalled menu so we can
  // set coords from the trigger's client rect directly.
  const [pos, setPos] = useState({ top: 0, left: 0, flipped: false, ready: false });

  // Which flat item indices are focusable (skip separators/sectionLabels/disabled)
  const focusable = useMemo(
    () => items
      .map((it, i) => (it.separator || it.sectionLabel || it.disabled ? null : i))
      .filter(v => v !== null),
    [items],
  );

  const focusIndex = (flatI) => {
    const el = itemRefs.current[flatI];
    if (el) el.focus();
  };

  // ─── Positioner ────────────────────────────────────────────────────────
  // Called on open, on window scroll, on window resize. Reads the trigger's
  // live rect + measured menu size; picks bottom-* if there is room, else
  // flips above (top-*); then shifts horizontally so it stays on-screen.
  const reposition = useCallback(() => {
    const t = triggerRef.current;
    const m = menuRef.current;
    if (!t || !m) return;

    const trigRect = t.getBoundingClientRect();
    const menuRect = m.getBoundingClientRect(); // measured after portal render
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - trigRect.bottom - VIEWPORT_PAD;
    const spaceAbove = trigRect.top - VIEWPORT_PAD;

    // Flip when the menu doesn't fit below AND there's more room above.
    // If neither has room, prefer the side with more space.
    let flipped = false;
    if (menuRect.height > spaceBelow && spaceAbove > spaceBelow) {
      flipped = true;
    }

    const top = flipped
      ? Math.max(VIEWPORT_PAD, trigRect.top - MENU_OFFSET - menuRect.height)
      : trigRect.bottom + MENU_OFFSET;

    // Horizontal placement — end of trigger by default, start when align="left".
    let left;
    if (align === "left") {
      left = trigRect.left;
    } else {
      left = trigRect.right - menuRect.width;
    }
    // Shift into viewport bounds.
    left = Math.min(
      Math.max(left, VIEWPORT_PAD),
      vw - menuRect.width - VIEWPORT_PAD,
    );

    setPos({ top, left, flipped, ready: true });
  }, [align]);

  // On open: measure once (menu is rendered off-screen initially via
  // `visibility: hidden` in the style until `ready=true`), then reposition
  // and mark ready. Rebind scroll/resize listeners while open.
  useLayoutEffect(() => {
    if (!open) return undefined;
    // First tick: menu isn't in the DOM yet on the render that flipped `open`
    // to true. Wait a frame so `menuRef.current` is attached.
    const rafId = window.requestAnimationFrame(() => {
      reposition();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [open, reposition, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    // capture:true so scrolls inside nested containers (like the users table)
    // also trigger a reposition, not just window scroll.
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, reposition]);

  // Dismiss on outside click + Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // Click inside the trigger or the portalled menu is considered inside.
      if (wrap.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    // Focus first focusable item on next tick (after positioning).
    const t = setTimeout(() => { if (focusable.length) focusIndex(focusable[0]); }, 20);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, focusable]);

  // Reset positioning-ready flag on close so the next open remeasures.
  useEffect(() => { if (!open) setPos((p) => ({ ...p, ready: false })); }, [open]);

  const activate = useCallback((it) => {
    if (it.disabled) return;
    setOpen(false);
    triggerRef.current?.focus();
    it.onClick?.();
  }, []);

  const onItemKeyDown = (e, currentFlatIdx) => {
    const flatI = focusable.indexOf(currentFlatIdx);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = focusable[(flatI + 1) % focusable.length];
      focusIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = focusable[(flatI - 1 + focusable.length) % focusable.length];
      focusIndex(prev);
    } else if (e.key === "Home") {
      e.preventDefault(); focusIndex(focusable[0]);
    } else if (e.key === "End") {
      e.preventDefault(); focusIndex(focusable[focusable.length - 1]);
    }
  };

  const onTriggerKeyDown = (e) => {
    if (["Enter", " ", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
    }
  };

  // Menu style — `fixed` because we portal to <body>. Hidden until measured
  // to avoid a one-frame flash at (0,0).
  const menuStyle = {
    position: "fixed",
    top: pos.top,
    left: pos.left,
    visibility: pos.ready ? "visible" : "hidden",
    zIndex: 10000,
  };

  return (
    <div ref={wrap} className="ds-dropdown" data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        className="ds-btn ds-btn--ghost ds-btn--sm ds-btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(v => !v)}
        onKeyDown={onTriggerKeyDown}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {trigger || <MoreHorizontal size={15} strokeWidth={2} />}
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <Menu
          ref={menuRef}
          style={menuStyle}
          align={align}
          ariaLabel={ariaLabel}
          testId={testId}
          dataFlipped={pos.flipped}
        >
          {items.map((it, i) => {
            if (it.separator)    return <div key={it.key} className="ds-dropdown__separator" role="separator" />;
            if (it.sectionLabel) return <div key={it.key} className="ds-dropdown__label" role="presentation">{it.sectionLabel}</div>;
            return (
              <button
                key={it.key}
                ref={(el) => { itemRefs.current[i] = el; }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={`ds-dropdown__item ${it.danger ? "ds-dropdown__item--danger" : ""}`}
                onClick={() => activate(it)}
                onKeyDown={(e) => onItemKeyDown(e, i)}
                disabled={it.disabled}
                data-testid={testId ? `${testId}-item-${it.key}` : undefined}
              >
                {it.icon && <span aria-hidden style={{ display: "inline-flex" }}>{it.icon}</span>}
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.shortcut && (
                  <kbd style={{ fontSize: 10, color: "var(--hbf-text-subtle)",
                                fontFamily: "var(--hbf-font-mono)" }}>{it.shortcut}</kbd>
                )}
              </button>
            );
          })}
        </Menu>,
        document.body,
      )}
    </div>
  );
}
